/**
 * Generates minimal schedule data for the timeline preview of a single task.
 *
 * The timeline requires a crop with at least one phase to render. Since we are
 * previewing a task only (viewStrategy='tasks'), a placeholder phase is included
 * to satisfy the data structure requirements — it is never rendered.
 *
 * @param {Object} params
 * @param {string} params.taskCode - Task type code (e.g. 'harvest')
 * @param {string} params.taskTypeName - Human-readable task type name
 * @param {import('dayjs').Dayjs} params.startDate - Task window start (dayjs)
 * @param {number} params.durationDays - Task window duration in days
 * @param {string} [params.notes] - Optional notes
 * @param {string} [params.orgId] - Organization ID (for display context)
 * @param {string} [params.cropId] - Crop ID (for crop-targeted tasks)
 *
 * @returns {Array} scheduleData array for CropTimeline scheduleData prop
 */
export function generatePreviewTaskData({
  taskCode,
  taskTypeName,
  startDate,
  durationDays,
  notes,
  orgId,
  cropId,
}) {
  if (!taskCode || !startDate) return []

  const startDateObj = startDate?.toDate ? startDate.toDate() : new Date(startDate)
  const endDateObj = new Date(startDateObj.getTime() + durationDays * 24 * 60 * 60 * 1000)

  return [
    {
      crop: {
        id: 'preview-task-crop',
        name: taskTypeName || taskCode,
        template_crop_species: {
          id: 'preview',
          name: taskTypeName || taskCode,
        },
        count: 1,
        organization_id: orgId || null,
      },
      schedule: {
        id: 'preview-task-schedule',
        crop_id: 'preview-task-crop',
        year: startDateObj.getFullYear(),
        planting_date: startDateObj.toISOString(),
        expected_harvest_date: endDateObj.toISOString(),
        state: 'active',
      },
      // Placeholder phase required so the crop appears in the timeline's phase-based
      // data structure. viewStrategy='tasks' ensures this is never rendered visually.
      phases: [
        {
          id: 'preview-phase-placeholder',
          phase_type_code: 'maturing',
          phase_type: { code: 'maturing', name: 'Maturing' },
          est_started_at: startDateObj.toISOString(),
          est_duration: `${durationDays} days`,
          step: 1,
          state: 'scheduled',
        },
      ],
      tasks: [
        {
          id: 'preview-task',
          task_code: taskCode,
          task_type: { name: taskTypeName || taskCode },
          task_window_start: startDateObj.toISOString(),
          duration: `${durationDays} days`,
          state: 'ready',
          notes: notes || '',
          organization_id: orgId || 'preview',
          crop_id: cropId || null,
        },
      ],
    },
  ]
}
